import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import type { PostListQuery, PostSort, PostType } from '@bb/shared';
import { count } from '@/lib/format';
import { useDebounced } from '@/lib/useDebounced';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { useTargets } from '@/features/targets/useTargets';
import { PostList } from '@/features/feed/PostList';
import { usePostActions, usePostStream } from '@/features/feed/usePosts';

const POST_TYPES: PostType[] = ['ORIGINAL', 'QUOTE', 'REPLY', 'REPOST'];

type MediaFilter = 'any' | 'with' | 'without';
type StateFilter = 'any' | 'unread' | 'read' | 'pinned';

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-sharp border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors',
        active
          ? 'border-accent/60 bg-accent-dim text-accent'
          : 'border-line text-ink-faint hover:border-line-bright hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

export function ArchivePage() {
  const targets = useTargets();

  const [search, setSearch] = useState('');
  const [targetId, setTargetId] = useState('');
  const [types, setTypes] = useState<PostType[]>([]);
  const [media, setMedia] = useState<MediaFilter>('any');
  const [state, setState] = useState<StateFilter>('any');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState<PostSort>('newest');

  const debouncedSearch = useDebounced(search.trim(), 350);

  const params = useMemo<PostListQuery>(() => {
    // Relevance ordering is meaningless without a query; fall back silently.
    const effectiveSort = sort === 'relevance' && !debouncedSearch ? 'newest' : sort;

    return {
      sort: effectiveSort,
      limit: 40,
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      ...(targetId ? { targetIds: [targetId] } : {}),
      ...(types.length > 0 ? { types } : {}),
      ...(media === 'with' ? { hasMedia: true } : {}),
      ...(media === 'without' ? { hasMedia: false } : {}),
      ...(state === 'unread' ? { isRead: false } : {}),
      ...(state === 'read' ? { isRead: true } : {}),
      ...(state === 'pinned' ? { isPinned: true } : {}),
      ...(from ? { from: new Date(`${from}T00:00:00Z`).toISOString() } : {}),
      ...(to ? { to: new Date(`${to}T23:59:59Z`).toISOString() } : {}),
    };
  }, [debouncedSearch, targetId, types, media, state, from, to, sort]);

  const stream = usePostStream('archive', params, {}, { liveArrivals: false });
  const actions = usePostActions(stream.patchLocal);

  const toggleType = (type: PostType) => {
    setTypes((current) =>
      current.includes(type) ? current.filter((item) => item !== type) : [...current, type],
    );
  };

  const reset = () => {
    setSearch('');
    setTargetId('');
    setTypes([]);
    setMedia('any');
    setState('any');
    setFrom('');
    setTo('');
    setSort('newest');
  };

  const hasFilters =
    Boolean(search || targetId || from || to) ||
    types.length > 0 ||
    media !== 'any' ||
    state !== 'any';

  return (
    <div className="grid h-full grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
      <Panel
        label="Query"
        className="min-h-0"
        bodyClassName="overflow-y-auto"
        actions={
          hasFilters ? (
            <Button size="sm" variant="ghost" onClick={reset} icon={<X size={11} />}>
              Clear
            </Button>
          ) : null
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Full text" hint="Quoted phrases and -exclusions are supported.">
            <div className="relative">
              <Search
                size={13}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="sanctions -rumour"
                className="pl-8"
              />
            </div>
          </Field>

          <Field label="Account">
            <Select value={targetId} onChange={(event) => setTargetId(event.target.value)}>
              <option value="">Any</option>
              {targets.data?.map((target) => (
                <option key={target.id} value={target.id}>
                  @{target.handle}
                </option>
              ))}
            </Select>
          </Field>

          <div className="flex flex-col gap-1.5">
            <span className="label-xs">Type</span>
            <div className="flex flex-wrap gap-1.5">
              {POST_TYPES.map((type) => (
                <Chip key={type} active={types.includes(type)} onClick={() => toggleType(type)}>
                  {type}
                </Chip>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="label-xs">Media</span>
            <div className="flex flex-wrap gap-1.5">
              {(['any', 'with', 'without'] as MediaFilter[]).map((option) => (
                <Chip key={option} active={media === option} onClick={() => setMedia(option)}>
                  {option}
                </Chip>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="label-xs">State</span>
            <div className="flex flex-wrap gap-1.5">
              {(['any', 'unread', 'read', 'pinned'] as StateFilter[]).map((option) => (
                <Chip key={option} active={state === option} onClick={() => setState(option)}>
                  {option}
                </Chip>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field label="From">
              <Input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </Field>
            <Field label="To">
              <Input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
            </Field>
          </div>

          <Field label="Order">
            <Select value={sort} onChange={(event) => setSort(event.target.value as PostSort)}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="relevance" disabled={!debouncedSearch}>
                Relevance
              </option>
            </Select>
          </Field>
        </div>
      </Panel>

      <Panel
        label="Results"
        flush
        className="min-h-0"
        actions={
          stream.total !== null ? (
            <Badge tone={stream.total > 0 ? 'accent' : 'neutral'}>
              {count(stream.total)} match{stream.total === 1 ? '' : 'es'}
            </Badge>
          ) : null
        }
        bodyClassName="min-h-0"
      >
        <PostList
          items={stream.items}
          isLoading={stream.query.isPending}
          error={stream.query.error}
          hasNextPage={stream.query.hasNextPage}
          isFetchingNextPage={stream.query.isFetchingNextPage}
          onLoadMore={() => void stream.query.fetchNextPage()}
          onRetry={() => void stream.query.refetch()}
          onTogglePin={actions.togglePin}
          onToggleRead={actions.toggleRead}
          emptyTitle={hasFilters ? 'No matches' : 'Archive empty'}
          emptyDetail={
            hasFilters
              ? 'Nothing in the archive satisfies this query. Loosen a filter and try again.'
              : 'Everything ingested is kept here indefinitely and stays searchable.'
          }
        />
      </Panel>
    </div>
  );
}
